/**
 * Weight input — compact numeric stepper used by paths/landers/offers.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';

import { NumberField } from '@/components/ui';
import { cn } from '@/lib/utils';

const MAX_INTEGER_WEIGHT = 999_999_999;

const READ_ONLY_SHELL =
  'inline-flex h-6 shrink-0 items-center justify-end rounded border border-dashed border-muted-foreground/40 bg-muted/20 px-1 text-right text-[11px] font-semibold tabular-nums leading-none text-muted-foreground';

export type WeightInputVariant = 'percent' | 'integer';

export interface WeightInputProps {
  value: number;
  onChange: (next: number) => void;
  ariaLabel: string;
  /**
   * `percent` — 0–100 with % suffix (normalized shares).
   * `integer` — non‑negative Voluum-style weights (no suffix).
   */
  variant?: WeightInputVariant;
  /** For `integer` variant only; path-level weights may use the default cap. */
  integerMax?: number;
  /** Voluum-managed weight: display only. */
  readOnly?: boolean;
  /** Native tooltip when `readOnly`. */
  readOnlyHint?: string;
}

const WeightInput: FC<WeightInputProps> = ({
  value,
  onChange,
  ariaLabel,
  variant = 'integer',
  integerMax,
  readOnly = false,
  readOnlyHint,
}) => {
  const intCap = integerMax ?? MAX_INTEGER_WEIGHT;
  const showPct = variant === 'percent';

  if (readOnly) {
    return (
      <span
        className={cn(
          READ_ONLY_SHELL,
          showPct ? 'w-[3.25rem]' : 'w-[3.5rem]',
        )}
        title={readOnlyHint}
        aria-label={`${ariaLabel} (read-only)`}
      >
        {value}
        {showPct ? (
          <span className="ml-px shrink-0 text-[9px] font-semibold leading-none text-muted-foreground">
            %
          </span>
        ) : null}
      </span>
    );
  }

  const max = showPct ? 100 : intCap;

  return (
    <div
      className={`inline-flex h-6 shrink-0 items-center rounded border border-input bg-background px-1 shadow-none focus-within:ring-1 focus-within:ring-ring ${showPct ? 'w-[3.25rem]' : 'w-[3.5rem]'}`}
    >
      <NumberField
        value={value}
        min={0}
        max={max}
        onChange={onChange}
        className="min-w-0 flex-1 bg-transparent py-0 text-right text-[11px] font-semibold tabular-nums leading-none outline-none"
        aria-label={ariaLabel}
      />
      {showPct ? (
        <span className="ml-px shrink-0 text-[9px] font-semibold leading-none text-muted-foreground">
          %
        </span>
      ) : null}
    </div>
  );
};

export default WeightInput;
